import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useServerFn } from "@tanstack/react-start";
import { useAuth } from "@/auth/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { PdfCanvasPreview } from "@/components/settings/PdfCanvasPreview";
import {
  Plus,
  FileText,
  Search,
  Receipt,
  DollarSign,
  ListChecks,
  Loader2,
  FileDown,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { useLocaleFormat } from "@/lib/i18n-format";
import { generateExpensesListPdf } from "@/utils/reports.functions";
import { downloadPdfFromBase64 } from "@/lib/report-export";
import { useScopedCompanyId } from "@/owner/useScopedCompanyId";
import { useOwnerCompany } from "@/owner/OwnerCompanyContext";
import { CompanyBadge } from "@/owner/CompanyBadge";

export const Route = createFileRoute("/_app/expenses/")({
  component: ExpensesIndex,
});

type ExpenseStatus = "pending" | "approved" | "rejected" | "reimbursed";

type Row = {
  id: string;
  number: string;
  category: string;
  vendor: string | null;
  description: string | null;
  amount: number;
  currency: string;
  expense_date: string;
  reimbursable: boolean;
  status: ExpenseStatus;
  receipt_path: string | null;
  project_id: string | null;
  company_id: string;
  projects: { id: string; name: string; code: string } | null;
};

const STATUS_VARIANT: Record<ExpenseStatus, "default" | "secondary" | "destructive" | "outline"> = {
  pending: "secondary",
  approved: "default",
  rejected: "destructive",
  reimbursed: "outline",
};

function ExpensesIndex() {
  const { t } = useTranslation();
  const { company } = useAuth();
  const { formatMoney, formatDate } = useLocaleFormat();
  const navigate = useNavigate();
  const scopedCompanyId = useScopedCompanyId();
  const { isOwner } = useOwnerCompany();
  const [rows, setRows] = useState<Row[]>([]);
  const [projects, setProjects] = useState<Array<{ id: string; name: string; code: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | ExpenseStatus>("all");
  const [projectFilter, setProjectFilter] = useState<string>("all");
  const [exportingPdf, setExportingPdf] = useState(false);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfPreviewBase64, setPdfPreviewBase64] = useState<string | null>(null);
  const [pdfPreviewFilename, setPdfPreviewFilename] = useState<string>("");
  const pdfFn = useServerFn(generateExpensesListPdf);

  useEffect(() => {
    // Owner viewing all companies has scopedCompanyId === null but isOwner === true.
    // Non-owner without company.id should not run.
    if (!isOwner && !company?.id) return;
    (async () => {
      setLoading(true);
      let expensesQ = supabase
        .from("expenses")
        .select(
          "id, number, category, vendor, description, amount, currency, expense_date, reimbursable, status, receipt_path, project_id, company_id, projects(id, name, code)",
        )
        .order("expense_date", { ascending: false });
      let projectsQ = supabase
        .from("projects")
        .select("id, name, code")
        .order("name", { ascending: true });
      if (scopedCompanyId) {
        expensesQ = expensesQ.eq("company_id", scopedCompanyId);
        projectsQ = projectsQ.eq("company_id", scopedCompanyId);
      }
      const [{ data, error }, { data: projectsData }] = await Promise.all([expensesQ, projectsQ]);
      if (error) toast.error(error.message);
      else setRows((data ?? []) as Row[]);
      setProjects((projectsData ?? []) as Array<{ id: string; name: string; code: string }>);
      setLoading(false);
    })();
  }, [scopedCompanyId, isOwner, company?.id]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (projectFilter !== "all") {
        if (projectFilter === "none" && r.project_id) return false;
        if (projectFilter !== "none" && r.project_id !== projectFilter) return false;
      }
      if (!q) return true;
      return (
        (r.vendor ?? "").toLowerCase().includes(q) ||
        (r.description ?? "").toLowerCase().includes(q) ||
        (r.category ?? "").toLowerCase().includes(q) ||
        (r.projects?.name ?? "").toLowerCase().includes(q)
      );
    });
  }, [rows, search, statusFilter, projectFilter]);

  const totals = useMemo(() => {
    const all = filtered.reduce((s, r) => s + Number(r.amount), 0);
    const pending = filtered
      .filter((r) => r.status === "pending")
      .reduce((s, r) => s + Number(r.amount), 0);
    const reimb = filtered
      .filter((r) => r.reimbursable && r.status !== "rejected" && r.status !== "reimbursed")
      .reduce((s, r) => s + Number(r.amount), 0);
    return { all, pending, reimb };
  }, [filtered]);

  const currency = company?.currency ?? "USD";

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t("pages.expenses.title")}</h1>
          <p className="text-sm text-muted-foreground">{t("pages.expenses.subtitle")}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={async () => {
              setExportingPdf(true);
              try {
                const ids = filtered.map((r) => r.id);
                const { pdfBase64, filename } = await pdfFn({
                  data: {
                    ids,
                    status: statusFilter,
                    projectId:
                      projectFilter === "all" || projectFilter === "none" ? null : projectFilter,
                  },
                });
                setPdfPreviewBase64(pdfBase64);
                setPdfPreviewFilename(filename || "despesas.pdf");
                setPdfPreviewOpen(true);
              } catch (e) {
                toast.error(e instanceof Error ? e.message : t("pages.expenses.pdfGenerateFailed"));
              } finally {
                setExportingPdf(false);
              }
            }}
            disabled={exportingPdf || filtered.length === 0}
          >
            {exportingPdf ? (
              <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <FileText className="mr-2 h-4 w-4" />
            )}
            {t("pages.expenses.previewPdf")}
          </Button>
          <Button onClick={() => navigate({ to: "/expenses/new" })}>
            <Plus className="mr-2 h-4 w-4" />
            {t("pages.expenses.new")}
          </Button>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <DollarSign className="h-4 w-4" /> {t("pages.expenses.total")}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-2xl font-semibold">
            {formatMoney(totals.all, currency)}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <ListChecks className="h-4 w-4" /> {t("pages.expenses.pending")}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-2xl font-semibold">
            {formatMoney(totals.pending, currency)}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Receipt className="h-4 w-4" /> {t("pages.expenses.reimbursable")}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-2xl font-semibold">
            {formatMoney(totals.reimb, currency)}
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("pages.expenses.searchPlaceholder")}
            className="pl-9"
          />
        </div>
        <Select
          value={statusFilter}
          onValueChange={(v) => setStatusFilter(v as typeof statusFilter)}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("pages.expenses.allStatuses")}</SelectItem>
            <SelectItem value="pending">{t("statuses.pending")}</SelectItem>
            <SelectItem value="approved">{t("statuses.approved")}</SelectItem>
            <SelectItem value="rejected">{t("statuses.rejected")}</SelectItem>
            <SelectItem value="reimbursed">{t("statuses.reimbursed")}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={projectFilter} onValueChange={setProjectFilter}>
          <SelectTrigger className="w-[220px]">
            <SelectValue placeholder={t("pages.expenses.filterByProject")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("pages.expenses.allProjects")}</SelectItem>
            <SelectItem value="none">{t("pages.expenses.noProject")}</SelectItem>
            {projects.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.code} · {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              {t("pages.expenses.loading")}
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center gap-3 p-12 text-center">
              <FileText className="h-10 w-10 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">{t("pages.expenses.empty")}</p>
              <Button size="sm" onClick={() => navigate({ to: "/expenses/new" })}>
                <Plus className="mr-2 h-4 w-4" />
                {t("pages.expenses.addFirst")}
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {filtered.map((r) => (
                <Link
                  key={r.id}
                  to="/expenses/$expenseId"
                  params={{ expenseId: r.id }}
                  className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 transition-colors hover:bg-muted/50"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs text-muted-foreground">{r.number}</span>
                      <span className="font-medium">
                        {r.vendor || r.description || t("pages.expenses.untitled")}
                      </span>
                      <CompanyBadge companyId={r.company_id} />
                      {r.receipt_path && (
                        <Receipt
                          className="h-3.5 w-3.5 text-muted-foreground"
                          aria-label={t("pages.expenses.hasReceipt")}
                        />
                      )}
                      {r.reimbursable && (
                        <Badge variant="outline" className="text-[10px]">
                          {t("pages.expenses.reimbursable")}
                        </Badge>
                      )}
                    </div>
                    <div className="mt-0.5 truncate text-xs text-muted-foreground">
                      {r.category}
                      {r.projects ? ` • ${r.projects.code} ${r.projects.name}` : ""}
                      {" • "}
                      {formatDate(r.expense_date)}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant={STATUS_VARIANT[r.status]}>{t(`statuses.${r.status}`)}</Badge>
                    <span className="w-24 text-right font-semibold tabular-nums">
                      {formatMoney(Number(r.amount), r.currency)}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={pdfPreviewOpen}
        onOpenChange={(open) => {
          if (!open) {
            setPdfPreviewOpen(false);
            setPdfPreviewBase64(null);
          }
        }}
      >
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle>{t("pages.expenses.pdfPreviewTitle")}</DialogTitle>
            <DialogDescription>
              {t("pages.expenses.pdfPreviewDesc", { filename: pdfPreviewFilename })}
            </DialogDescription>
          </DialogHeader>
          <div className="h-[70vh] w-full overflow-hidden rounded-md border border-border bg-muted">
            {pdfPreviewBase64 ? (
              <PdfCanvasPreview pdfBase64={pdfPreviewBase64} />
            ) : (
              <div className="flex h-full items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setPdfPreviewOpen(false);
                setPdfPreviewBase64(null);
              }}
            >
              {t("common.close")}
            </Button>
            <Button
              onClick={() => {
                if (!pdfPreviewBase64) return;
                downloadPdfFromBase64(pdfPreviewFilename || "despesas.pdf", pdfPreviewBase64);
                toast.success(t("pages.expenses.pdfDownloaded"));
              }}
              disabled={!pdfPreviewBase64}
              className="gap-2"
            >
              <FileDown className="h-4 w-4" />
              {t("pages.expenses.downloadPdf")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
